import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideAddTicketPopup, setTickets } from "../../Pages/Store";
import { toast } from "react-toastify";
import { RiDeleteBin2Line, RiUploadCloud2Line, RiSendPlaneLine } from "react-icons/ri";
import { BsInfoCircle } from "react-icons/bs";
import { Select, MenuItem } from "@mui/material";
import { createTicket, getAllTickets, getErrorMessage } from "../../services/api";

const priorityColors = {
  Urgent: "#dc2626",
  High: "#f97316",
  Medium: "#eab308",
  Low: "#16a34a",
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = (error) => reject(error);
  });
};


const AddTicket = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("choose");
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  const dispatch = useDispatch();
  const fileInputRef = useRef(null); // Reference to the file input

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);

    const maxSizeMB = 5; // Max size per file

    // Allowed file extensions
    const allowedExtensions = new Set([
     'png', 'jpg', 'jpeg', 'gif', 'tiff', 'pdf',
    'xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx',
    'txt', 'html', 'json', 'csv'])

    // Convert MB to bytes
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (selectedFiles.length > 1) {
      setFormErrors({ ...formErrors, file: "Only one attachment is allowed" });
      fileInputRef.current.value = null; // Clear input field
      return;
    }

    const file = selectedFiles[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (!allowedExtensions.has(fileExtension)) {
      setFormErrors({ ...formErrors, file: "Unsupported file format" });
      fileInputRef.current.value = null; // Clear input field
      return;
    }

    if (file.size > maxSizeBytes) {
      setFormErrors({ ...formErrors, file: `Maximum upload file size: ${maxSizeMB} MB` });
      fileInputRef.current.value = null; // Clear input field
      return;
    }

    setFiles([file]); // Replace any previously selected file — only one allowed
    setFormErrors((prevErrors) => {
      const { file, ...rest } = prevErrors;
      return rest;
    });
  };

  const ticketType = useSelector((state) => state.root.ticketType);
  

  const handleFileDelete = (fileToDelete) => {
    setFiles(files.filter((file) => file !== fileToDelete));
    // Clear the file input field when file is deleted
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const validateForm = () => {
    const errors = {};
    const trimmedTitle = title?.trim();
    if (!trimmedTitle) {
      errors.title = "Title required";
    } else if (trimmedTitle.length > 250) {
      errors.title = "Title cannot exceed 250 characters";
    }
    if (!description?.trim()) errors.description = "Description required";

      if (priority === "choose" && ticketType ==="Incident") errors.priority = "Select valid priority";
      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    };
    

    

  const submitNewTicket = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { username, password } = JSON.parse(
        localStorage.getItem("ssCreds") || "{}"
      );

      const ticketData = {
        Ticket: {
          Type: ticketType,
          Title: title,
          State: "New",
          Priority: ticketType === "Incident" ? priority : "Medium",
          CustomerUser: username,
        },
        Article: {
          Subject: title,
          Body: description,
          ContentType: "text/plain; charset=utf-8",
        },
      };

      if (files.length > 0) {
        ticketData.Attachment = await Promise.all(
          files.map(async (file) => ({
            Content: await fileToBase64(file),
            ContentType: file.type || "application/octet-stream",
            Filename: file.name,
          }))
        );
      }

      await createTicket(username, password, ticketData);

      toast.success("Ticket created successfully", {
        position: "top-right",
        autoClose: 3000,
      });

      const ticketResponse = await getAllTickets(username, password);
      dispatch(setTickets(ticketResponse.data.tickets));

      dispatch(hideAddTicketPopup());
    } catch (error) {
      const message = getErrorMessage(error, "Failed to add ticket");
      toast.error(message, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    dispatch(hideAddTicketPopup());
  };

  return (
    <div id="addTicketPopup">
              {loading && (
          <div class="text-center">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        )}

      <div className="card popup">
        <div className="details-header">
          <div>
            <h5>New Ticket</h5>
            <p className="ss-form-subtitle">
              {ticketType ? `${ticketType} · ` : ""}Fill in the details below to create a support ticket
            </p>
          </div>
          <span
            className="closeDetails"
            onClick={handleCancel}
            title="Close"
          >
            &times;
          </span>
        </div>
        <div className="card-body text-left">
          <form>
            <div className="form-group mb-3">
              <label htmlFor="ticketTitle" className="ss-form-label">
                Title<span className="required">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${formErrors.title ? "is-invalid" : ""}`}
                placeholder="Briefly describe the issue"
                id="ticketTitle"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              {formErrors.title && (
                <span className="field-error">{formErrors.title}</span>
              )}
            </div>
            <div className="form-group mb-3">
              <label htmlFor="ticketDescription" className="ss-form-label">
                Description<span className="required">*</span>
              </label>
              <textarea
                className={`form-control ${formErrors.description ? "is-invalid" : ""}`}
                id="ticketDescription"
                rows="3"
                placeholder="Add as much detail as possible"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              ></textarea>
              {formErrors.description && (
                <span className="field-error">
                  {formErrors.description}
                </span>
              )}
            </div>
            { ticketType==="Incident" && (
            <div className="form-group severity mb-3">
              <label htmlFor="severity" className="ss-form-label">
                Priority<span className="required">*</span>
              </label>
              <div className="severity-select">
              <Select
                className="ss-mui-select"
                id="severity"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                variant="outlined"
                size="small"
                slotProps={{ paper: { className: "ss-select-menu-paper" } }}
              >
                <MenuItem value="choose">Choose...</MenuItem>
                <MenuItem value="Urgent">
                  <span className="status-dot-inline" style={{ background: priorityColors.Urgent }}></span>
                  Urgent
                </MenuItem>
                <MenuItem value="High">
                  <span className="status-dot-inline" style={{ background: priorityColors.High }}></span>
                  High
                </MenuItem>
                <MenuItem value="Medium">
                  <span className="status-dot-inline" style={{ background: priorityColors.Medium }}></span>
                  Medium
                </MenuItem>
                <MenuItem value="Low">
                  <span className="status-dot-inline" style={{ background: priorityColors.Low }}></span>
                  Low
                </MenuItem>
              </Select>
              <span
                className="ss-info-icon"
                data-toggle="tooltip" data-placement="top"
                title="
                Urgent : System is down
High : Product is working, functionality or workflow is broken
Medium : Product is functional, user is facing minor or intermittent product issue
Low : Product is functional with no apparent issues" ><BsInfoCircle /></span>
            </div>
            {formErrors.priority && (
              <span className="field-error mt-5">{formErrors.priority}</span>
            )}
            </div>
            )}

            <div className="form-group mb-3">
              <label htmlFor="fileUpload" className="ss-form-label">Add File (Optional)</label>
              <input
                type="file"
                id="fileUpload"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <div
                className="ss-upload-dropzone"
                onClick={() => fileInputRef.current?.click()}
                title="File Formats supported: png, jpg, jpeg, gif, tiff, pdf, xls, xlsx, doc, docx, ppt, pptx, txt, html, json, csv"
              >
                <RiUploadCloud2Line className="ss-upload-icon" />
                <span className="ss-upload-text">
                  Click to upload <span className="ss-upload-text-muted">or drag and drop</span>
                </span>
                <span className="ss-upload-hint">PNG, JPG, PDF, DOC, XLS, PPT, TXT, CSV up to 5MB</span>
              </div>
              {formErrors.file && (
                <span className="field-error">{formErrors.file}</span>
              )}
              <div>
                {files.length > 0  && files.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-item-name">{file.name}</span>
                    <span
                      onClick={() => handleFileDelete(file)}
                      className="file-item-remove"
                    >
                      <RiDeleteBin2Line title="Remove file" />
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="Button-grps">
              <button
                type="button"
                className="btn btn-light add-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-ss-primary"
                onClick={submitNewTicket}
                disabled={loading}
              >
                <RiSendPlaneLine /> Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTicket;
